import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Grid,
  Typography,
  CircularProgress,
  LinearProgress,
  Fade,
  Grow,
  useTheme,
  keyframes
} from '@mui/material';

// Loading states for different content types
export const LoadingStates = {
  STORIES: 'stories',
  EVENTS: 'events',
  COMMENTS: 'comments',
  PROFILE: 'profile',
  SEARCH: 'search'
};

// Animated pulse effect
const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
`;

// Story Card Skeleton - matches the actual story card layout
export const StoryCardSkeleton = ({ animate = true }) => {
  const theme = useTheme();
  
  return (
    <Fade in timeout={300}>
      <Card 
        sx={{ 
          height: '100%',
          animation: animate ? `${pulse} 2s ease-in-out infinite` : 'none',
          '&:hover': {
            transform: 'translateY(-2px)',
            transition: 'transform 0.2s ease-in-out'
          }
        }}
      >
        <CardContent>
          {/* Title skeleton */}
          <Skeleton 
            variant="text" 
            width="80%" 
            height={32} 
            sx={{ mb: 1 }}
          />
          
          {/* Author skeleton */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
          
          {/* Content preview skeleton */}
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="75%" height={20} sx={{ mb: 2 }} />
          
          {/* Tags skeleton */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Skeleton variant="rounded" width={60} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
            <Skeleton variant="rounded" width={50} height={24} />
          </Box>
          
          {/* Stats skeleton */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="text" width={60} height={20} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
            <Skeleton variant="text" width={100} height={20} />
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

// Grid of story card skeletons
export const StoriesGridSkeleton = ({ count = 6 }) => {
  return (
    <Grid container spacing={3}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} md={6} lg={4} key={index}>
          <Grow in timeout={300 + (index * 100)}>
            <div>
              <StoryCardSkeleton />
            </div>
          </Grow>
        </Grid>
      ))}
    </Grid>
  );
};

// Enhanced loading spinner with text
export const LoadingSpinner = ({ 
  message = "Loading...", 
  size = 40, 
  showProgress = false,
  progress = 0 
}) => {
  return (
    <Fade in timeout={300}>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          py: 4,
          gap: 2
        }}
      >
        <CircularProgress size={size} thickness={4} />
        <Typography variant="body1" color="text.secondary">
          {message}
        </Typography>
        {showProgress && (
          <Box sx={{ width: '200px' }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {Math.round(progress)}% complete
            </Typography>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

// Inline loading for buttons and small components
export const InlineLoading = ({ size = 20, message }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={size} thickness={4} />
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Box>
  );
};

// Page loading overlay
export const PageLoadingOverlay = ({ message = "Loading page..." }) => {
  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        <LoadingSpinner message={message} size={60} />
      </Box>
    </Fade>
  );
};

// Smart loading component that shows different states
export const SmartLoading = ({ 
  type = 'stories', 
  count = 6, 
  message,
  enableSkeleton = true,
  timeout = 1000 
}) => {
  const [showSkeleton, setShowSkeleton] = React.useState(enableSkeleton);
  
  React.useEffect(() => {
    if (enableSkeleton) {
      const timer = setTimeout(() => {
        setShowSkeleton(false);
      }, timeout);
      
      return () => clearTimeout(timer);
    }
  }, [timeout, enableSkeleton]);
  
  if (type === 'stories' && showSkeleton) {
    return <StoriesGridSkeleton count={count} />;
  }
  
  return (
    <LoadingSpinner 
      message={message || `Loading ${type}...`} 
      size={50}
    />
  );
};

// Progressive loading messages
export const getLoadingMessage = (type, phase = 'initial') => {
  const messages = {
    [LoadingStates.STORIES]: {
      initial: 'Loading stories...',
      searching: 'Searching stories...',
      filtering: 'Filtering results...',
      complete: 'Stories loaded!'
    },
    [LoadingStates.EVENTS]: {
      initial: 'Loading events...',
      searching: 'Finding events...',
      filtering: 'Organizing events...',
      complete: 'Events loaded!'
    },
    [LoadingStates.COMMENTS]: {
      initial: 'Loading comments...',
      posting: 'Posting comment...',
      updating: 'Updating comment...',
      complete: 'Comments loaded!'
    },
    [LoadingStates.PROFILE]: {
      initial: 'Loading profile...',
      updating: 'Updating profile...',
      saving: 'Saving changes...',
      complete: 'Profile loaded!'
    },
    [LoadingStates.SEARCH]: {
      initial: 'Searching...',
      processing: 'Processing results...',
      ranking: 'Ranking matches...',
      complete: 'Search complete!'
    }
  };
  
  return messages[type]?.[phase] || messages[type]?.initial || 'Loading...';
};

export default {
  StoryCardSkeleton,
  StoriesGridSkeleton,
  LoadingSpinner,
  InlineLoading,
  PageLoadingOverlay,
  SmartLoading,
  LoadingStates,
  getLoadingMessage
};
